import { useAuthStore } from "@/stores/use-auth-store";
import type { User } from "@/types";
import { APP_CONSTANTS } from "@/config";
import {
  usePaywallStore,
  type FeatureLimitPayload,
} from "@/stores/use-paywall-store";
import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from "axios";

const httpRequest = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "Content-type": "application/json",
  },
});

export interface BrowserSession {
  token: string | null;
  tenant: string | null;
  revision: number;
}

export interface SessionRequestConfig extends AxiosRequestConfig {
  _session?: BrowserSession;
  _skipSessionRecovery?: boolean;
}

function browserSession(): BrowserSession {
  return {
    token: localStorage.getItem(APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY),
    tenant: localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY),
    revision: useAuthStore.getState().sessionRevision,
  };
}

function isCurrentSession(session: BrowserSession): boolean {
  const current = browserSession();
  return session.token === current.token && session.tenant === current.tenant
    && session.revision === current.revision;
}

function sessionChanged(): AxiosError {
  return new AxiosError("Browser session changed", "ERR_CANCELED");
}

function isAuthEndpoint(url: string | undefined): boolean {
  return /(?:^|\/)(?:login|refresh|logout)(?:[?#]|$)/.test(url || "");
}

// Bind retries to the original credential/tenant instead of reading a new login.
httpRequest.interceptors.request.use(async (request) => {
  const config = request as RetryConfig;
  config._session ??= browserSession();
  if (!isCurrentSession(config._session)) throw sessionChanged();
  const pending = !config._skipSessionRecovery && !isAuthEndpoint(config.url)
    ? useAuthStore.getState().getPendingRefresh() : undefined;
  if (pending) {
    await pending;
    const recovered = browserSession();
    if (recovered.tenant !== config._session.tenant || recovered.revision !== config._session.revision) {
      throw sessionChanged();
    }
    config._session = recovered;
  }
  if (config._session.token && !config._skipSessionRecovery && !isAuthEndpoint(config.url)
    && config.url !== "/me" && useAuthStore.getState().sessionStatus !== "ready") {
    throw new AxiosError("Session identity unavailable", "ERR_SESSION_UNVERIFIED");
  }
  config.headers["X-Tenant"] = config._session.tenant || "";
  if (config._session.token) {
    config.headers.Authorization = "Bearer " + config._session.token;
  } else {
    config.headers.delete("Authorization");
  }
  return config;
});

// Response interceptor: retry with exponential backoff on 5xx / network errors
const MAX_RETRIES = 3;
const RETRY_METHODS = new Set(["get", "head", "options"]);

interface RetryConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
  _authRetried?: boolean;
  _session?: BrowserSession;
  _skipSessionRecovery?: boolean;
}

/**
 * A 5xx that carries a machine-readable `code` is a refusal the backend
 * decided, not an outage: `meta_not_configured` and its siblings answer the
 * same way to the tenth attempt as to the first. Retrying one only delays the
 * message — connecting Instagram on a platform with no Meta application spent
 * about ten seconds in "Conectando..." before the card could say why
 * (2026-09-11).
 *
 * A code-less 500/502/503/504 stays retryable: that is a proxy, a cold
 * container or a restart, and those do recover between attempts.
 */
function isDeterministicRefusal(data: unknown): boolean {
  if (typeof data !== "object" || data === null) return false;
  const code = (data as { code?: unknown }).code;
  return typeof code === "string" && code.length > 0;
}

httpRequest.interceptors.response.use(
  (response) => {
    const session = (response.config as RetryConfig)._session;
    if (session && !isCurrentSession(session)) throw sessionChanged();
    // Check for feature usage warnings from backend
    const warning = response.headers["x-feature-warning"];
    if (warning) {
      try {
        const data = JSON.parse(warning);
        window.dispatchEvent(
          new CustomEvent("feature-limit-warning", { detail: data })
        );
      } catch {
        // ignore malformed header
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as RetryConfig | undefined;

    // 429 + Feature_limit_reached → open paywall (CheckPlanFeature middleware
    // structured payload, see SP-011). Done before the early-returns below so
    // the paywall opens even on POST/PUT/DELETE — the only place users hit
    // the cap is on a write to a gated endpoint.
    const status = error.response?.status;
    const body = error.response?.data as
      | { error?: string; data?: FeatureLimitPayload }
      | undefined;
    if (status === 429 && body?.error === "Feature_limit_reached" && body.data) {
      usePaywallStore.getState().openFromApi429(body.data);
    }

    if (!config) return Promise.reject(error);

    const session = config._session;
    if (!session || !isCurrentSession(session)) return Promise.reject(sessionChanged());

    const method = (config.method || "get").toLowerCase();
    const safeRead = RETRY_METHODS.has(method);
    const authEndpoint = isAuthEndpoint(config.url);

    // Recover authentication independently of retrying the business operation.
    // A failed write remains failed: only safe reads may be automatically replayed.
    if (status === 401 && !authEndpoint && !config._skipSessionRecovery && session.token) {
      if (config._authRetried) return Promise.reject(error);
      config._authRetried = true;
      await useAuthStore.getState().refreshToken();
      const recovered = browserSession();
      if (recovered.tenant !== session.tenant || recovered.revision !== session.revision) {
        return Promise.reject(sessionChanged());
      }
      if (!safeRead) return Promise.reject(error);
      config._session = recovered;
      return httpRequest(config);
    }

    if (!safeRead || authEndpoint || config._skipSessionRecovery) return Promise.reject(error);
    const isRetryable = !status || (status >= 500 && !isDeterministicRefusal(error.response?.data));
    if (!isRetryable) return Promise.reject(error);

    config._retryCount = (config._retryCount || 0) + 1;
    if (config._retryCount > MAX_RETRIES) return Promise.reject(error);

    // Exponential backoff: 1s, 2s, 4s (+ jitter)
    const delay = Math.min(1000 * 2 ** (config._retryCount - 1), 8000);
    const jitter = delay * 0.2 * Math.random();
    await new Promise((r) => setTimeout(r, delay + jitter));

    return httpRequest(config);
  }
);

/** Identity reconciliation must not refresh or log out a newer browser session. */
export async function fetchSessionIdentity(token: string, tenant: string | null): Promise<User> {
  const response = await axios.post<User>("/me", {}, {
    baseURL: httpRequest.defaults.baseURL,
    headers: { Authorization: `Bearer ${token}`, "X-Tenant": tenant || "", Accept: "application/json" },
  });
  return response.data;
}

/** Revoke the captured credential after local logout; never recover or replay it. */
export async function revokeSessionCredential(token: string, tenant: string | null): Promise<void> {
  await axios.post("/logout", {}, {
    baseURL: httpRequest.defaults.baseURL,
    adapter: httpRequest.defaults.adapter,
    headers: { Authorization: "Bearer " + token, "X-Tenant": tenant || "", Accept: "application/json" },
    timeout: 15_000,
  });
}

export { httpRequest };
